import cx from 'classnames';
import React, { Children, cloneElement } from 'react';

import useGetUid from '@skippay/core/hooks/useGetUid';

import classes from './index.module.sass';

const isInputElement = (
	child: React.ReactNode | React.ReactFragment | React.ReactPortal
): child is React.ReactElement<{ id?: string; placeholder?: string }, `input`> =>
	React.isValidElement(child) && (child.type === `input` || child.type === `textarea`);

const FieldContainerWithAnimatedLabel: React.FC<{
	animatedLabel: React.ReactNode;
	children: React.ReactNode;
}> = ({ animatedLabel, children }) => {
	const getSyntheticId = useGetUid(`field`);
	let fieldId;
	const childrenWithId = Children.toArray(children).map((child) => {
		if (!isInputElement(child)) return child;
		fieldId = child.props.id || getSyntheticId();
		if (process.env.NODE_ENV === `development` && child.props.placeholder) {
			// eslint-disable-next-line no-console
			console.warn(
				`When the 'animatedPlaceholder' is set, the 'placeholder' property of an input is ignored.`
			);
		}
		return cloneElement(child, { id: fieldId, placeholder: ` ` });
	});
	return (
		<>
			{childrenWithId}
			<label className={classes.animatedLabel} htmlFor={fieldId}>
				<span>{animatedLabel}</span>
			</label>
		</>
	);
};

const FieldContainer: React.FC<Props> = ({
	animatedLabel,
	bold,
	children,
	className,
	hasSensitiveContent,
	...other
}) => {
	return (
		<div
			{...other}
			className={cx(classes.container, bold && classes.bold, className)}
			{...(hasSensitiveContent && { 'data-clarity-mask': true })}
		>
			{animatedLabel ? (
				<FieldContainerWithAnimatedLabel animatedLabel={animatedLabel}>
					{children}
				</FieldContainerWithAnimatedLabel>
			) : (
				children
			)}
		</div>
	);
};

export type Props = {
	animatedLabel?: React.ReactNode;
	children: React.ReactNode;
	className?: string;
	bold?: boolean;
	onClick?: React.MouseEventHandler;
	hasSensitiveContent?: boolean;
	[key: string]: unknown;
};

export default FieldContainer;
